/**
 * `useCanAccessAdmin` — does the current user have the persisted
 * superadmin flag? Drives the admin sidebar entry visibility and the
 * superadmin-mode toggle visibility in the user menu (#429 Wave 2).
 *
 * Reads the same `IdentitySummary` source as every other identity
 * gate (no new endpoint). The flag is the persisted Better Auth
 * `isSuperadmin` bit — orthogonal to escalation. A `true` here means
 * "this user CAN escalate"; the actual escalation (the
 * `x-superadmin-mode: on` header that backend routes consult) is
 * tracked separately by `auth/superadminMode.ts` and exposed via
 * `useSuperadminMode()`. Components that need both should pull both
 * hooks — e.g. the Header's toggle is rendered when this hook returns
 * true, and reflects the current toggle state via `useSuperadminMode`.
 *
 * Fail-closed: returns `false` while identity is loading or in error.
 * Same shape as `useCanI` — the UI shouldn't differentiate "loading"
 * from "denied" for an affordance check.
 *
 * Anti-pattern this avoids: inlining `identity.user.isSuperadmin`
 * checks in Sidebar / route guards. A future change (e.g. layering an
 * additional condition like "is in admin mode AND on a specific
 * route") needs exactly one body to edit.
 */
import { useIdentity } from './useIdentity'

export function useCanAccessAdmin(): boolean {
  const { state } = useIdentity()
  if (state.status !== 'ready') return false
  return state.identity.user.isSuperadmin === true
}
